import { useState } from 'react';

export interface Address {
  id: number;
  user_id: number;
  nom_adresse: string;
  adresse_complete: string;
  point_repere: string | null;
  quartier: string | null;
  ville: string | null;
  pays: string | null;
  code_postal: string | null;
  est_principale: boolean;
  icon: string;
  created_at?: string;
  updated_at?: string;
}

interface SavedAddressesProps {
  addresses: Address[];
  onAddAddress: (address: Omit<Address, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => void | Promise<void>;
  onEditAddress: (id: number, address: Omit<Address, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => void | Promise<void>;
  onDeleteAddress: (id: number) => void | Promise<void>;
  onSelectAddress: (address: Address) => void;
  loading: boolean;
  error: string;
}

export default function SavedAddresses({
  addresses,
  onAddAddress,
  onEditAddress,
  onDeleteAddress,
  onSelectAddress,
  loading,
  error
}: SavedAddressesProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    icon: 'ri-home-line',
    isDefault: false,
    pointRepere: ''
  });
  const [formError, setFormError] = useState('');

  const iconOptions = [
    { value: 'ri-home-line', label: 'Domicile', color: 'text-blue-600' },
    { value: 'ri-building-line', label: 'Bureau', color: 'text-green-600' },
    { value: 'ri-group-line', label: 'Famille', color: 'text-purple-600' },
    { value: 'ri-heart-line', label: 'Favori', color: 'text-red-600' },
    { value: 'ri-map-pin-line', label: 'Autre', color: 'text-gray-600' }
  ];

  const landmarks = [
    { id: 'marche_madina', name: 'Marché Madina', address: 'Madina, Conakry' },
    { id: 'marche_taouyah', name: 'Marché Taouyah', address: 'Taouyah, Conakry' },
    { id: 'gare_routiere', name: 'Gare Routière', address: 'Conakry' },
    { id: 'aeroport_gb', name: 'Aéroport GB', address: 'Gbessia, Conakry' },
    { id: 'stade_28_septembre', name: 'Stade 28 Septembre', address: 'Conakry' },
    { id: 'palais_peuple', name: 'Palais du Peuple', address: 'Conakry' },
    { id: 'universite_gamal', name: 'Université Gamal Abdel Nasser', address: 'Conakry' },
    { id: 'hopital_donka', name: 'Hôpital Donka', address: 'Conakry' },
    { id: 'carrefour_mamou', name: 'Carrefour Mamou', address: 'Conakry' },
    { id: 'kaloum', name: 'Kaloum (Centre-ville)', address: 'Kaloum, Conakry' },
    { id: 'ratoma', name: 'Ratoma', address: 'Ratoma, Conakry' },
    { id: 'matam', name: 'Matam', address: 'Matam, Conakry' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name || !formData.address) {
      setFormError('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    // Le payload correspond exactement à Omit<Address, 'id' | 'user_id' | 'created_at' | 'updated_at'>
    const payload = {
      nom_adresse: formData.name,
      adresse_complete: formData.address,
      est_principale: formData.isDefault,
      icon: formData.icon, // Ajouté pour respecter la structure d'Address
      point_repere: formData.pointRepere || null,
      quartier: null,
      ville: null,
      pays: null,
      code_postal: null,
    };

    try {
      if (editingAddress) {
        await onEditAddress(editingAddress.id, payload);
      } else {
        await onAddAddress(payload);
      }
      resetForm();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Une erreur est survenue.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      icon: 'ri-home-line',
      isDefault: false,
      pointRepere: ''
    });
    setShowAddModal(false);
    setEditingAddress(null);
    setFormError('');
  };

  const startEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData({
      name: address.nom_adresse,
      address: address.adresse_complete,
      icon: address.icon || 'ri-home-line',
      isDefault: address.est_principale || false,
      pointRepere: address.point_repere || ''
    });
    setShowAddModal(true);
  };

  const handleDeleteClick = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette adresse ?')) {
      try {
        await onDeleteAddress(id);
      } catch (err: unknown) {
        setFormError(err instanceof Error ? err.message : 'Échec de la suppression de l\'adresse.');
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Adresses enregistrées</h3>
        <button
          onClick={() => { setShowAddModal(true); setEditingAddress(null); setFormError(''); }}
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <i className="ri-add-line"></i>
          <span>Ajouter</span>
        </button>
      </div>

      {loading && (
        <div className="text-center py-8">
          <p className="text-gray-500">Chargement des adresses...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
          <strong className="font-bold">Erreur:</strong>
          <span className="block sm:inline ml-2">{error}</span>
        </div>
      )}

      {!loading && !error && addresses.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-map-pin-line text-gray-400 text-2xl"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">Aucune adresse enregistrée</h4>
          <p className="text-gray-600 mb-4">Ajoutez vos adresses fréquentes pour commander plus rapidement</p>
          <button
            onClick={() => { setShowAddModal(true); setEditingAddress(null); setFormError(''); }}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Ajouter ma première adresse
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {!loading && addresses.map((address) => (
            <div key={address.id} className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className={`${address.icon || 'ri-map-pin-line'} text-teal-600`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-gray-900">{address.nom_adresse}</h4>
                      {address.est_principale && (
                        <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full font-medium">
                          Par défaut
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{address.adresse_complete}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={() => onSelectAddress(address)}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Utiliser
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(address)}
                    className="text-gray-400 hover:text-gray-600 p-2"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(address.id)}
                    className="text-red-400 hover:text-red-600 p-2"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal d'ajout/édition d'adresse */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingAddress ? 'Modifier l\'adresse' : 'Ajouter une adresse'}
              </h3>
              <button
                type="button"
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {formError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
                <strong className="font-bold">Erreur:</strong>
                <span className="block sm:inline ml-2">{formError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'adresse
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Domicile, Bureau, Famille..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icône
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {iconOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: option.value })}
                      className={`p-3 border rounded-lg flex items-center justify-center transition-colors ${formData.icon === option.value
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <i className={`${option.value} ${option.color} text-xl`}></i>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Point de repère (optionnel)
                </label>
                <select
                  value={formData.pointRepere || ''}
                  onChange={(e) => {
                    const selectedLandmark = landmarks.find(l => l.id === e.target.value);
                    if (selectedLandmark) {
                      setFormData({
                        ...formData,
                        pointRepere: selectedLandmark.name,
                        address: selectedLandmark.address
                      });
                    } else {
                      setFormData({ ...formData, pointRepere: '' });
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Sélectionner un lieu connu...</option>
                  {landmarks.map((landmark) => (
                    <option key={landmark.id} value={landmark.id}>
                      {landmark.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse complète
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Entrez l'adresse complète..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  required
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                  Définir comme adresse par défaut
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  {editingAddress ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}